'use client'

import { useAtomValue } from 'jotai'
import { selectAtom } from 'jotai/utils'
import { CircleHelp } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { MarketStatusNotice } from '@/components/widget/market-status-notice'
import { OptionRow } from '@/features/chart/components/widgets/option-row'
import { OptionRowContextMenu } from '@/features/chart/components/widgets/option-row-context-menu'
import { useOptionChain } from '@/features/chart/hooks/use-option-chain-table'
import {
  CALL_COLUMN_ORDER,
  OPTION_TABLE_COLUMNS,
  OptionTableColumn,
  PUT_COLUMN_ORDER,
} from '@/features/chart/lib/configs/option-setting-config'
import {
  buildStrikeSymbolMap,
  clampStrikeIndexRange,
  collectWindowedOptionSymbols,
  computeVisibleStrikeRange,
  estimateFallbackStrikeIndex,
  resolveStrikeRangeForKey,
  type StrikeIndexRange,
} from '@/features/chart/lib/option-chain-stream-windowing'
import { groupOptionPositionsBySymbol } from '@/features/chart/lib/option-position-display'
import { sameOptionPositionFields } from '@/features/chart/lib/option-position-equality'
import {
  buildOpenOptionTabArgs,
  getStrikesForExpiry,
  parseExpiryDate,
  resolveOptionSymbol,
} from '@/features/chart/lib/utils/option-util'
import { useWatchlistTab } from '@/features/dashboard/hook/use-watchlist-tab'
import { resolveBaseCurrencyFromTicker } from '@/features/order/order-form/lib/instrument-resolution'
import { useCardAccountContext } from '@/hooks/use-card-account'
import {
  useLinkingGroupField,
  useLinkingGroupOptionUnderlyingPrice,
  useLinkingGroupWatchList,
} from '@/hooks/use-linking-group-state'
import { useScrollChaining } from '@/hooks/use-scroll-chaining'
import { useTradingVenueScope } from '@/hooks/use-trading-venue-scope'
import { exchangeToTradeVenue } from '@/lib/configs/exchange-registry'
import { matchesTradingVenue } from '@/lib/trading-venue-scope'
import { cn } from '@/lib/utils'
import { formatLocalizedDate, toLocalDateFromUtcDate } from '@/lib/utils/date-locale'
import { watchHorizontalScrollIntent } from '@/lib/utils/horizontal-scroll-intent'
import { useUserAccounts } from '@/service/account.hook'
import { useMultiIncrementalInstrumentStream } from '@/service/ws/use-incremental-instrument'
import { OPTION_CHAIN_VISIBILITY_STALE_THRESHOLD_MS } from '@/service/ws/visibility-policy'
import { allOpenOrdersAtom, allPositionsAtom } from '@/state/account'
import { optionTableConfigAtom } from '@/state/chart'
import { aggregatePositionsAtom } from '@/state/position-group'
import { OrderSide, OrderStatus } from '@/types/generated/common/trading'
import {
  Exchange,
  IncrementalTickerList,
  Instrument,
  MarketDataType,
} from '@/types/generated/common/types'

// Mirrors websocket-context.tsx's RESUBSCRIBE_CHUNK_SIZE/_DELAY_MS — a wide/deep
// chain's full symbol list is chunked and staggered the same way when force-refreshed
// on tab return, for the same "don't blast the server with one giant message" reason.
const RESUBSCRIBE_CHUNK_SIZE = 40
const RESUBSCRIBE_CHUNK_DELAY_MS = 150

const OPTION_ROW_HEIGHT = 36 // Matches the ATM auto-scroll effect's own row-height math.
// 500 is the shared, connection-wide subscription cap (service/ws/websocket-context.tsx's
// subscriptionsRef) — not a budget this widget alone owns, and it's multiplied by however
// many OptionTable instances happen to be open at once. Kept conservative so other
// widgets (and other open option chains) still have room. This single value is used both
// as "when to start windowing" (a total at or under it is already within budget, so it's
// safe to subscribe in full) and as the hard cap applied to the windowed result.
const OPTION_STREAM_BUDGET = 100
const OPTION_STREAM_RANGE_BUFFER_ROWS = 10
// Keep a much wider DOM window than the live-data subscription window. Expiry changes
// and position/ATM jumps can move a long way through a deep chain; retaining 100 rows
// on either side prevents those programmatic moves from landing outside the mounted
// row window while the stream remains bounded by OPTION_STREAM_BUDGET.
const OPTION_RENDER_RANGE_BUFFER_ROWS = 100
const OPTION_INITIAL_RENDER_ROWS = 40
// No Virtuoso here (unlike the watchlist wizard), so there's no already-throttled
// rangeChanged callback to piggyback on — this scroll container is a plain DOM element,
// so the debounce has to be built here.
const OPTION_STREAM_RANGE_DEBOUNCE_MS = 150
const EMPTY_STRIKE_RANGE: StrikeIndexRange = { startIndex: 0, endIndex: -1 }

interface KeyedStrikeRange extends StrikeIndexRange {
  key: string
}

const formatExpiryDisplay = (expiry: string, locale: string): string => {
  const yearOfNow = new Date().getFullYear()
  const date = parseExpiryDate(expiry)
  if (date.getTime() === 0) return expiry // Invalid format fallback

  const localDate = toLocalDateFromUtcDate(date)

  return formatLocalizedDate(
    localDate,
    locale,
    yearOfNow === localDate.getFullYear() ? 'expiryTab' : 'expiryTabWithYear'
  )
}

// Header Cell Component with click-to-show popover
const HeaderCell = ({ columnKey }: { columnKey: keyof OptionTableColumn }) => {
  const t = useTranslations('trade.optionColumns')
  const meta = OPTION_TABLE_COLUMNS[columnKey]
  const abbr = t(`columns.${columnKey}.abbr`)

  if (!meta.description) {
    return <div className="whitespace-nowrap">{abbr}</div>
  }

  return (
    <div className="flex items-center justify-center gap-0.5 whitespace-nowrap">
      <span>{abbr}</span>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-1p-ignore
            aria-label={t(`columns.${columnKey}.description`)}
            className="text-foreground/30 hover:text-foreground/60 inline-flex cursor-pointer transition-colors"
          >
            <CircleHelp className="size-2.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent side="top" className="w-auto max-w-50 p-2 px-3 text-xs">
          {t(`columns.${columnKey}.description`)}
        </PopoverContent>
      </Popover>
    </div>
  )
}

// Get decimal places for underlying price based on ticker
const getUnderlyingPriceDecimals = (ticker: string): { min: number; max: number } => {
  const baseCurrency = resolveBaseCurrencyFromTicker([], ticker)
  if (baseCurrency === 'BTC') return { min: 0, max: 0 }
  if (baseCurrency === 'ETH') return { min: 1, max: 1 }
  return { min: 0, max: 2 }
}

// Floating Underlying Price Indicator
const UnderlyingPriceIndicator = ({
  underlyingPrice,
  sortedStrikes,
  selectedTicker,
}: {
  underlyingPrice: number
  sortedStrikes: number[]
  selectedTicker: string
}) => {
  // Find the index where underlyingPrice should be positioned
  const priceIndex = sortedStrikes.findIndex((strike, index) => {
    const nextStrike = sortedStrikes[index + 1]
    return underlyingPrice >= strike && (nextStrike === undefined || underlyingPrice < nextStrike)
  })

  if (priceIndex === -1) return null

  // Row height 36px
  const rowHeight = 36
  const topPosition = (priceIndex + 1) * rowHeight

  const decimals = getUnderlyingPriceDecimals(selectedTicker)

  return (
    <div
      className="pointer-events-none absolute top-0 right-0 left-0 z-10 flex items-center transition-transform duration-150 ease-out"
      style={{ transform: `translateY(${topPosition}px) translateY(-50%)` }}
    >
      <span className="border-foreground/20 my-auto h-0 flex-1 border-t border-dashed" />
      <div className="bg-foreground/5 pointer-events-auto flex h-7 w-16 origin-center scale-101 items-center justify-center rounded-full py-0 shadow-sm backdrop-blur-xs dark:shadow-none">
        <div className="text-ox-cyan z-10 rounded-full text-[10px] font-bold">
          {underlyingPrice.toLocaleString('en-US', {
            minimumFractionDigits: decimals.min,
            maximumFractionDigits: decimals.max,
          })}
        </div>
      </div>
      <span className="border-foreground/20 my-auto h-0 flex-1 border-t border-dashed" />
    </div>
  )
}

interface OptionTableProps {
  instruments: Instrument[]
  cardId: string
  focusTicker: string | null
  exchange: Exchange
}

function OptionTable({ instruments, cardId, focusTicker, exchange }: OptionTableProps) {
  const t = useTranslations('trade.optionColumns')
  const locale = useLocale()
  const {
    selectedTicker,
    selectedExpiry,
    rows,
    grouped,
    availableExpiries,
    setSelectedExpiry,
    handleTickers,
    subscriptionSymbols,
    optionSymbolMap,
  } = useOptionChain(cardId, instruments, focusTicker, exchange)

  // Strike indices belong to one exact ticker+expiry array. Treat a range from any
  // other chain as empty until this chain's scroll measurement publishes its own.
  const strikeRangeKey = `${selectedTicker ?? ''}-${selectedExpiry ?? ''}`

  // Derive sortedStrikes directly from instruments data, not from rows.
  // rows updates asynchronously (via effect), but grouped updates in the same render
  // as selectedExpiry — so sortedStrikes is always correct for the current expiry.
  // Declared early (moved up from its previous spot further down) because streamPayloads
  // below needs it too.
  const sortedStrikes = useMemo(() => {
    if (!selectedTicker || !selectedExpiry) return []
    const group = grouped.get(selectedTicker)
    if (!group) return []
    return getStrikesForExpiry(group, selectedExpiry).sort((a, b) => a - b)
  }, [selectedTicker, selectedExpiry, grouped])

  // strike -> {call, put} symbol lookup built from the same source as
  // subscriptionSymbols (grouped.expires entries), independent of `rows` — see
  // buildStrikeSymbolMap's doc comment for why it can't be sourced from `rows`.
  const strikeSymbolsByStrike = useMemo(
    () =>
      buildStrikeSymbolMap(
        grouped.get(selectedTicker ?? '')?.expires.get(selectedExpiry ?? '') ?? []
      ),
    [grouped, selectedTicker, selectedExpiry]
  )

  // Rendering and wire-subscription windows intentionally move at different speeds:
  // DOM rows follow scroll at most once per animation frame, while subscription churn
  // remains debounced. Sharing one range made fast scrolling show the previous window
  // until the 150ms network debounce elapsed.
  const [renderStrikeWindow, setRenderStrikeWindow] = useState<KeyedStrikeRange>({
    key: '',
    ...EMPTY_STRIKE_RANGE,
  })
  const [subscriptionStrikeWindow, setSubscriptionStrikeWindow] = useState<KeyedStrikeRange>({
    key: '',
    ...EMPTY_STRIKE_RANGE,
  })
  const renderStrikeRange = resolveStrikeRangeForKey(
    renderStrikeWindow,
    renderStrikeWindow.key,
    strikeRangeKey
  )
  const subscriptionStrikeRange = resolveStrikeRangeForKey(
    subscriptionStrikeWindow,
    subscriptionStrikeWindow.key,
    strikeRangeKey
  )
  const clampedRenderStrikeRange = useMemo(
    () =>
      clampStrikeIndexRange(renderStrikeRange, sortedStrikes.length, OPTION_INITIAL_RENDER_ROWS),
    [renderStrikeRange, sortedStrikes.length]
  )
  const renderedStrikes = useMemo(
    () =>
      sortedStrikes
        .slice(clampedRenderStrikeRange.startIndex, clampedRenderStrikeRange.endIndex + 1)
        .map((strike, offset) => ({
          index: clampedRenderStrikeRange.startIndex + offset,
          strike,
        })),
    [clampedRenderStrikeRange, sortedStrikes]
  )
  const updateRenderStrikeRange = useCallback(
    (next: StrikeIndexRange) => {
      setRenderStrikeWindow((prev) =>
        prev.key === strikeRangeKey &&
        prev.startIndex === next.startIndex &&
        prev.endIndex === next.endIndex
          ? prev
          : { key: strikeRangeKey, ...next }
      )
    },
    [strikeRangeKey]
  )
  const updateSubscriptionStrikeRange = useCallback(
    (next: StrikeIndexRange) => {
      setSubscriptionStrikeWindow((prev) =>
        prev.key === strikeRangeKey &&
        prev.startIndex === next.startIndex &&
        prev.endIndex === next.endIndex
          ? prev
          : { key: strikeRangeKey, ...next }
      )
    },
    [strikeRangeKey]
  )

  const watchList = useLinkingGroupWatchList(cardId)
  const focusedOptionStrike = useLinkingGroupField(cardId, 'focusedOptionStrike')
  const { optionUnderlyingPrice: underlyingPrice } = useLinkingGroupOptionUnderlyingPrice(cardId)
  const { openInstrumentTab } = useWatchlistTab(cardId)
  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    symbol: string
  } | null>(null)
  const config = useAtomValue(optionTableConfigAtom)
  const visibleColumns = config.columns
  const { tradeVenue: cardTradeVenue, accountId } = useCardAccountContext(cardId)
  const { data: userAccountsData } = useUserAccounts({ showDisabled: true })
  const userAccounts = useMemo(() => userAccountsData?.accounts ?? [], [userAccountsData])
  const accountLabelById = useMemo(
    () => new Map(userAccounts.map((account) => [account.accountId, account.name])),
    [userAccounts]
  )
  const venueScope = useTradingVenueScope(exchangeToTradeVenue(exchange) ?? cardTradeVenue)
  // Chart account selection remains singular: the option-chain position and order
  // indicators follow the same venue-scoped account selected in the chart header —
  // unless "Aggregate positions" is on, in which case every account on this venue
  // counts (the per-strike summary already sums them, with a per-account tooltip).
  const aggregatePositions = useAtomValue(aggregatePositionsAtom)
  const positionsAccountId = aggregatePositions ? undefined : accountId
  const positionsAtom = useMemo(
    () =>
      selectAtom(
        allPositionsAtom,
        (allPositions) =>
          positionsAccountId
            ? allPositions.filter((position) => position.accountId === positionsAccountId)
            : allPositions.filter((position) => matchesTradingVenue(position, venueScope)),
        sameOptionPositionFields
      ),
    [venueScope, positionsAccountId]
  )
  const positions = useAtomValue(positionsAtom)
  const allOpenOrders = useAtomValue(allOpenOrdersAtom)
  const openOrders = useMemo(
    () =>
      accountId
        ? allOpenOrders.filter((item) => item.order?.accountId === accountId)
        : allOpenOrders.filter((item) => item.order && matchesTradingVenue(item.order, venueScope)),
    [allOpenOrders, venueScope, accountId]
  )

  // Compute active orders by symbol and side for quick lookup
  const activeOrdersMap = useMemo(() => {
    const map = new Map<string, { buy: boolean; sell: boolean }>()
    openOrders.forEach((item) => {
      const order = item.order
      if (!order || !order.symbol) return
      // Only consider active orders
      if (
        order.status === OrderStatus.ORDER_STATUS_FILLED ||
        order.status === OrderStatus.ORDER_STATUS_CANCELLED ||
        order.status === OrderStatus.ORDER_STATUS_REJECTED
      ) {
        return
      }

      const current = map.get(order.symbol) || { buy: false, sell: false }
      if (order.side === OrderSide.ORDER_SIDE_BUY) {
        current.buy = true
      }
      if (order.side === OrderSide.ORDER_SIDE_SELL) {
        current.sell = true
      }
      map.set(order.symbol, current)
    })
    return map
  }, [openOrders])

  // Compute positions map by symbol for quick lookup
  const positionsMap = useMemo(() => groupOptionPositionsBySymbol(positions), [positions])

  const handleExpiryChange = useCallback(
    (expiry: string) => {
      setSelectedExpiry(expiry)
    },
    [setSelectedExpiry]
  )

  // Stable identity: OptionRow's memo comparator checks this by reference, so an inline
  // arrow here made every row fail the comparison on every render of this table.
  const handleContextMenuRequest = useCallback((x: number, y: number, symbol: string) => {
    setContextMenu({ x, y, symbol })
  }, [])

  // Get visible column keys in order
  const visibleCallColumns = useMemo(
    () => CALL_COLUMN_ORDER.filter((key) => visibleColumns[key]),
    [visibleColumns]
  )
  const visiblePutColumns = useMemo(
    () => PUT_COLUMN_ORDER.filter((key) => visibleColumns[key]),
    [visibleColumns]
  )
  const visibleColCount = visibleCallColumns.length

  // Symbols missing from optionSymbolMap fall back to a regex parse; cache every
  // resolution for the life of this map so re-deriving the per-expiry summaries below
  // doesn't re-parse the same symbols again.
  const resolveSymbol = useMemo(() => {
    const cache = new Map<string, ReturnType<typeof resolveOptionSymbol>>()
    return (symbol: string) => {
      if (cache.has(symbol)) return cache.get(symbol) ?? null
      const parsed = resolveOptionSymbol(symbol, optionSymbolMap)
      cache.set(symbol, parsed)
      return parsed
    }
  }, [optionSymbolMap])

  // Per-expiry long/short size totals for the selected ticker
  const expiryPositionSizes = useMemo(() => {
    const map = new Map<string, { long: number; short: number }>()
    if (!selectedTicker) return map
    for (const pos of positions) {
      const parsed = resolveSymbol(pos.symbol)
      if (!parsed || parsed.ticker !== selectedTicker) continue
      const size = parseFloat(pos.quantity) || 0
      const current = map.get(parsed.expiry) ?? { long: 0, short: 0 }
      if (pos.side === OrderSide.ORDER_SIDE_BUY) {
        current.long += size
      } else {
        current.short += size
      }
      map.set(parsed.expiry, current)
    }
    return map
  }, [positions, selectedTicker, resolveSymbol])

  // Per-expiry presence of open orders for the selected ticker
  const expiryHasOrders = useMemo(() => {
    const set = new Set<string>()
    if (!selectedTicker) return set
    openOrders.forEach((item) => {
      const order = item.order
      if (!order || !order.symbol) return
      if (
        order.status === OrderStatus.ORDER_STATUS_FILLED ||
        order.status === OrderStatus.ORDER_STATUS_CANCELLED ||
        order.status === OrderStatus.ORDER_STATUS_REJECTED
      )
        return

      const parsed = resolveSymbol(order.symbol)
      if (parsed && parsed.ticker === selectedTicker) {
        set.add(parsed.expiry)
      }
    })
    return set
  }, [openOrders, selectedTicker, resolveSymbol])

  // Calculate dynamic min-width based on visible columns
  // Base: 860px for 14 columns (7 per side) + 1 strike
  // Scale proportionally: 860 / 15 = ~57.3px per column
  const totalCols = visibleColCount * 2 + 1
  const BASE_MIN_WIDTH = 860
  const BASE_TOTAL_COLS = 15 // 7 + 1 + 7
  const COL_WIDTH = BASE_MIN_WIDTH / BASE_TOTAL_COLS
  const dynamicMinWidth = Math.max(BASE_MIN_WIDTH, totalCols * COL_WIDTH)

  // Above the budget, the live subscription is windowed to a slice of strikes around
  // the current scroll position rather than the whole expiry (see streamPayloads
  // below) — the ATM auto-scroll effect uses this to know when it must bootstrap the
  // window with a guess instead of trusting an arbitrary/stale scroll position.
  const isWindowedChain = subscriptionSymbols.length > OPTION_STREAM_BUDGET

  // Create payloads for multi-stream subscription. Below OPTION_STREAM_BUDGET, subscribe
  // the whole expiry like before — above it (a wide/deep chain, e.g. OPRA equity
  // options), window down to the visible strikes + buffer, hard-capped at the budget.
  const streamPayloads = useMemo(() => {
    if (!isWindowedChain) {
      return subscriptionSymbols.map((symbol) => ({
        symbol,
        exchange,
        dataType: MarketDataType.MARKET_DATA_TYPE_INCREMENTAL_TICKER,
      }))
    }
    const symbols = collectWindowedOptionSymbols({
      sortedStrikes,
      strikeSymbols: strikeSymbolsByStrike,
      range: subscriptionStrikeRange,
      maxSymbols: OPTION_STREAM_BUDGET,
    })
    return symbols.map((symbol) => ({
      symbol,
      exchange,
      dataType: MarketDataType.MARKET_DATA_TYPE_INCREMENTAL_TICKER,
    }))
  }, [
    subscriptionSymbols,
    isWindowedChain,
    sortedStrikes,
    strikeSymbolsByStrike,
    subscriptionStrikeRange,
    exchange,
  ])

  // Overlap subscription window: keep prev + current expiry symbols subscribed during transition.
  // When expiry changes, different expiries have no symbol overlap, so the WebSocket hook would
  // normally unsub → wait 100ms → resub (no-overlap path), risking snapshot message loss.
  // By including previous symbols in the new subscription, the hook detects overlap and
  // subscribes to new symbols immediately alongside the old ones, eliminating the 100ms gap.
  const lastStreamPayloadsRef = useRef(streamPayloads)
  const overlapCleanupRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [activePayloads, setActivePayloads] = useState(streamPayloads)

  // Subscribe to incremental ticker stream for all option symbols
  const { flushAndReconnect } = useMultiIncrementalInstrumentStream({
    connected: activePayloads.length > 0,
    payloads: activePayloads,
    onMessage: (data: IncrementalTickerList | undefined) => {
      if (!data?.incrementalTickers) return
      handleTickers(data.incrementalTickers)
    },
  })

  const isLoading = sortedStrikes.length === 0 && !!selectedTicker && !!selectedExpiry

  // Ref for scroll container
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const rowsContainerRef = useRef<HTMLDivElement>(null)
  const expiryListRef = useRef<HTMLDivElement>(null)
  const lastScrolledKeyRef = useRef<string | null>(null)
  // Tracks the scrollKey a one-time windowed-chain bootstrap scroll (see the ATM
  // effect below) has already been applied for — separate from lastScrolledKeyRef,
  // which tracks the real ATM/focused-strike scroll. Deliberately not merged: once
  // underlyingPrice/focusedOptionStrike resolves, the effect must still re-run and
  // override this interim guess.
  const fallbackAnchoredKeyRef = useRef<string | null>(null)
  const underlyingPriceRef = useRef(underlyingPrice)

  useEffect(() => {
    underlyingPriceRef.current = underlyingPrice
  }, [underlyingPrice])

  const [isScrolled, setIsScrolled] = useState(false)
  const isScrolledRef = useRef(false)

  useScrollChaining(scrollContainerRef, { immediate: true })

  useEffect(() => {
    expiryListRef.current?.scrollTo({ left: 0 })
  }, [availableExpiries, selectedTicker])

  useEffect(() => {
    if (overlapCleanupRef.current !== null) {
      clearTimeout(overlapCleanupRef.current)
    }

    const prevPayloads = lastStreamPayloadsRef.current
    const seen = new Set<string>()
    const combined = [...prevPayloads, ...streamPayloads].filter((p) => {
      if (seen.has(p.symbol)) return false
      seen.add(p.symbol)
      return true
    })
    lastStreamPayloadsRef.current = streamPayloads

    // If the union of the previous and new expiry's payloads would itself exceed the
    // budget (each side can already be close to it for a wide/deep chain), skip the
    // overlap grace period entirely and switch straight to the new payloads — the
    // overlap boost only exists to avoid a ~100ms snapshot gap for typical small chains,
    // and isn't worth risking the shared connection's subscription cap for.
    const useOverlap = combined.length <= OPTION_STREAM_BUDGET
    setActivePayloads(useOverlap ? combined : streamPayloads)

    if (useOverlap) {
      overlapCleanupRef.current = setTimeout(() => {
        setActivePayloads(streamPayloads)
        overlapCleanupRef.current = null
      }, 300)
    }

    return () => {
      if (overlapCleanupRef.current !== null) clearTimeout(overlapCleanupRef.current)
    }
  }, [streamPayloads])

  // On tab return after a long-enough absence, force a fresh snapshot for every
  // currently-subscribed symbol. mergeTickerUpdate (use-option-chain-table.ts) only
  // ever merges fields present in an incoming message — it never clears a field back
  // to unknown — so a field with no update since before the tab was hidden keeps
  // showing its pre-hidden value indefinitely even once new messages resume. This is
  // what got reported as the chain "feels cached" after 5-10 min away.
  useEffect(() => {
    let hiddenAt: number | null = null
    // Chunk delays are staggered via setTimeout below; none of these were ever
    // tracked, so an unmount (or a fast tab-close) while a stagger was still
    // pending let it fire anyway and call flushAndReconnect on a chain the
    // user had already navigated away from.
    const pendingTimeouts: ReturnType<typeof setTimeout>[] = []

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now()
        return
      }

      const wasHiddenAt = hiddenAt
      hiddenAt = null
      if (wasHiddenAt === null) return
      if (Date.now() - wasHiddenAt < OPTION_CHAIN_VISIBILITY_STALE_THRESHOLD_MS) return

      // refreshSubscriptions sends one unsub + one resub message per call (see
      // subscription-refresh.ts), so a wide/deep chain (100+ symbols) refreshed in
      // one shot risks the same oversized-batch/rate-limit issue resubscribeAll()
      // in websocket-context.tsx was chunked for. Chunk + stagger here too.
      const payloads = lastStreamPayloadsRef.current
      for (let i = 0; i < payloads.length; i += RESUBSCRIBE_CHUNK_SIZE) {
        const chunk = payloads.slice(i, i + RESUBSCRIBE_CHUNK_SIZE)
        const delay = (i / RESUBSCRIBE_CHUNK_SIZE) * RESUBSCRIBE_CHUNK_DELAY_MS
        pendingTimeouts.push(setTimeout(() => flushAndReconnect(chunk), delay))
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      pendingTimeouts.forEach((id) => clearTimeout(id))
    }
  }, [flushAndReconnect])

  // underlyingPrice often arrives after sortedStrikes is ready (async ticker stream).
  // Track its availability so the ATM scroll effect re-runs once it becomes available.
  const hasUnderlyingPrice = underlyingPrice != null

  useEffect(() => {
    if (!scrollContainerRef.current) return

    const container = scrollContainerRef.current
    const activeItemId = watchList?.list[watchList?.selectedIndex]?.id ?? ''
    // Include focusedOptionStrike._id so each click forces a fresh scroll even on the same ticker/expiry
    const scrollKey = `${selectedTicker}-${selectedExpiry}-${activeItemId}-${focusedOptionStrike?._id ?? 0}`
    const isNewKey = lastScrolledKeyRef.current !== scrollKey

    if (sortedStrikes.length === 0) return

    let targetIndex = -1

    // focusedOptionStrike: row click from external widgets (position table, order table)
    // Watch-list opens skip this and fall through to ATM
    if (
      focusedOptionStrike &&
      focusedOptionStrike.ticker === selectedTicker &&
      focusedOptionStrike.expiry === selectedExpiry
    ) {
      targetIndex = sortedStrikes.indexOf(focusedOptionStrike.strike)
    }

    // ATM fallback — read from ref to get the latest price without depending on every tick
    if (targetIndex === -1) {
      const price = underlyingPriceRef.current
      if (price) {
        targetIndex = sortedStrikes.findIndex((strike, index) => {
          const nextStrike = sortedStrikes[index + 1]
          return price >= strike && (nextStrike === undefined || price < nextStrike)
        })
        // price is below all strikes (deep OTM expiry) — snap to nearest (first)
        if (targetIndex === -1 && sortedStrikes.length > 0) {
          targetIndex = 0
        }
      }
    }

    const stickyHeight = rowsContainerRef.current ? rowsContainerRef.current.offsetTop : 56

    if (targetIndex === -1) {
      // No focused strike and no price yet. For a windowed (wide/deep) chain,
      // `container.scrollTop` here is either 0 (a freshly reset chain) or a stale
      // pixel offset left over from a previous ticker/expiry — and underlyingPrice
      // itself only ever arrives via a ticker for an ALREADY-subscribed symbol
      // (use-option-chain-table.ts's processTickers). If whatever's under that
      // arbitrary position happens to be an illiquid/dead corner of the chain, no
      // ticker ever arrives, underlyingPrice never gets set, and this effect never
      // gets a reason to re-run and correct course — the chain hangs indefinitely
      // until the user manually scrolls. Bootstrap once with a much safer guess
      // (the chain's middle strike — real chains list strikes roughly symmetric
      // around spot) so a ticker snapshot reliably arrives and unblocks the real
      // ATM scroll below. Narrow chains don't need this: every symbol is already
      // subscribed regardless of range, so an arbitrary position is harmless there.
      if (
        isWindowedChain &&
        isNewKey &&
        fallbackAnchoredKeyRef.current !== scrollKey &&
        sortedStrikes.length > 0
      ) {
        fallbackAnchoredKeyRef.current = scrollKey
        const fallbackIndex = estimateFallbackStrikeIndex(sortedStrikes.length)
        const fallbackPosition = (fallbackIndex + 1) * OPTION_ROW_HEIGHT
        const fallbackVisibleHeight = Math.max(0, container.clientHeight - stickyHeight)
        container.scrollTop = Math.max(0, fallbackPosition - fallbackVisibleHeight / 2)
      }

      // Fall back to whatever's actually on screen right now (possibly the
      // bootstrap position just applied above) instead of leaving the range at its
      // stale/empty value.
      const currentRenderRange = computeVisibleStrikeRange({
        scrollTop: container.scrollTop,
        clientHeight: container.clientHeight,
        headerOffset: stickyHeight,
        rowHeight: OPTION_ROW_HEIGHT,
        bufferRows: OPTION_RENDER_RANGE_BUFFER_ROWS,
      })
      const currentSubscriptionRange = computeVisibleStrikeRange({
        scrollTop: container.scrollTop,
        clientHeight: container.clientHeight,
        headerOffset: stickyHeight,
        rowHeight: OPTION_ROW_HEIGHT,
        bufferRows: OPTION_STREAM_RANGE_BUFFER_ROWS,
      })
      updateRenderStrikeRange(currentRenderRange)
      updateSubscriptionStrikeRange(currentSubscriptionRange)
      return
    }

    // Do not force the subscription back to ATM when this effect re-runs for an
    // unrelated dependency change. Once the initial/focused scroll has completed,
    // the visible range belongs to the user's current scroll position.
    if (!isNewKey) return

    const targetPosition = (targetIndex + 1) * OPTION_ROW_HEIGHT
    const visibleHeight = Math.max(0, container.clientHeight - stickyHeight)
    const targetScrollTop = Math.max(0, targetPosition - visibleHeight / 2)

    // Apply the programmatic scroll before publishing either window. The old smooth,
    // delayed scroll raced the scroll effect's initial debounce and ResizeObserver:
    // both could overwrite the target subscription with the previous scroll position.
    // Reading scrollTop back also accounts for the browser clamping near either edge.
    container.scrollTop = targetScrollTop
    const settledRenderRange = computeVisibleStrikeRange({
      scrollTop: container.scrollTop,
      clientHeight: container.clientHeight,
      headerOffset: stickyHeight,
      rowHeight: OPTION_ROW_HEIGHT,
      bufferRows: OPTION_RENDER_RANGE_BUFFER_ROWS,
    })
    const settledSubscriptionRange = computeVisibleStrikeRange({
      scrollTop: container.scrollTop,
      clientHeight: container.clientHeight,
      headerOffset: stickyHeight,
      rowHeight: OPTION_ROW_HEIGHT,
      bufferRows: OPTION_STREAM_RANGE_BUFFER_ROWS,
    })
    updateRenderStrikeRange(settledRenderRange)
    updateSubscriptionStrikeRange(settledSubscriptionRange)
    lastScrolledKeyRef.current = scrollKey
    fallbackAnchoredKeyRef.current = null
  }, [
    sortedStrikes,
    selectedTicker,
    selectedExpiry,
    focusedOptionStrike,
    watchList?.list,
    watchList?.selectedIndex,
    hasUnderlyingPrice,
    isWindowedChain,
    updateRenderStrikeRange,
    updateSubscriptionStrikeRange,
  ])

  useEffect(() => {
    return () => {
      if (overlapCleanupRef.current !== null) clearTimeout(overlapCleanupRef.current)
    }
  }, [])

  // Keep the strike column centred horizontally — through widget/browser resizes and
  // column changes — until the user scrolls sideways themselves; vertical scrolling
  // doesn't count. Resizes of the container (viewport) and the rows (content width) both
  // re-centre.
  useEffect(() => {
    const container = scrollContainerRef.current
    const rowsContainer = rowsContainerRef.current
    if (!container || !rowsContainer) return
    const center = () => {
      const overflow = container.scrollWidth - container.clientWidth
      if (overflow > 0) container.scrollLeft = overflow / 2
    }
    const resizeObserver = new ResizeObserver(center)
    resizeObserver.observe(container)
    resizeObserver.observe(rowsContainer)
    const stopIntentWatch = watchHorizontalScrollIntent(container, container, () =>
      resizeObserver.disconnect()
    )
    return () => {
      resizeObserver.disconnect()
      stopIntentWatch()
    }
  }, [])

  // The render window follows scrolling on requestAnimationFrame for immediate visual
  // feedback. Only the stream subscription window keeps the 150ms debounce, avoiding
  // subscribe/unsubscribe churn while still keeping DOM work within the viewport.
  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    let rangeDebounceTimer: ReturnType<typeof setTimeout> | null = null
    let renderFrame: number | null = null

    const computeCurrentRange = (bufferRows: number) => {
      const headerOffset = rowsContainerRef.current?.offsetTop ?? 56
      return computeVisibleStrikeRange({
        scrollTop: el.scrollTop,
        clientHeight: el.clientHeight,
        headerOffset,
        rowHeight: OPTION_ROW_HEIGHT,
        bufferRows,
      })
    }

    const updateRenderRange = () => {
      renderFrame = null
      updateRenderStrikeRange(computeCurrentRange(OPTION_RENDER_RANGE_BUFFER_ROWS))
    }

    const onScroll = () => {
      const nextIsScrolled = el.scrollTop > 0
      if (isScrolledRef.current !== nextIsScrolled) {
        isScrolledRef.current = nextIsScrolled
        setIsScrolled(nextIsScrolled)
      }

      if (renderFrame === null) renderFrame = requestAnimationFrame(updateRenderRange)

      // Independent of render virtualization: wire subscriptions should settle only
      // after scrolling pauses, not churn on every animation frame.
      if (rangeDebounceTimer) clearTimeout(rangeDebounceTimer)
      rangeDebounceTimer = setTimeout(
        () => updateSubscriptionStrikeRange(computeCurrentRange(OPTION_STREAM_RANGE_BUFFER_ROWS)),
        OPTION_STREAM_RANGE_DEBOUNCE_MS
      )
    }

    onScroll()
    el.addEventListener('scroll', onScroll, { passive: true })

    const resizeObserver = new ResizeObserver(() => {
      updateRenderStrikeRange(computeCurrentRange(OPTION_RENDER_RANGE_BUFFER_ROWS))
      updateSubscriptionStrikeRange(computeCurrentRange(OPTION_STREAM_RANGE_BUFFER_ROWS))
    })
    resizeObserver.observe(el)

    return () => {
      if (rangeDebounceTimer) clearTimeout(rangeDebounceTimer)
      if (renderFrame !== null) cancelAnimationFrame(renderFrame)
      el.removeEventListener('scroll', onScroll)
      resizeObserver.disconnect()
    }
  }, [updateRenderStrikeRange, updateSubscriptionStrikeRange])

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* Expiry Selector */}
      <div
        className={cn([
          'border-foreground/10 z-30 shrink-0 border-b backdrop-blur-md',
          isScrolled ? 'bg-background/20' : 'bg-foreground/3',
        ])}
      >
        <div className="flex min-w-0 items-center gap-2 pr-2">
          <div
            ref={expiryListRef}
            className="scrollbar-none min-w-0 flex-1 overflow-x-auto overscroll-x-contain"
          >
            {selectedTicker && availableExpiries.length > 0 && (
              <div className="flex w-max min-w-full gap-1 p-2">
                {availableExpiries.map((expiry) => {
                  const posSize = expiryPositionSizes.get(expiry)
                  const hasOrder = expiryHasOrders.has(expiry)
                  const longSize = posSize?.long ?? 0
                  const shortSize = posSize?.short ?? 0
                  const total = longSize + shortSize
                  const hasPosition = total > 0

                  return (
                    <button
                      key={expiry}
                      type="button"
                      data-1p-ignore
                      data-selected={selectedExpiry === expiry}
                      onClick={() => handleExpiryChange(expiry)}
                      className={cn([
                        'relative flex flex-col items-center gap-0.5 px-2 py-1 text-xs transition-colors data-[selected=false]:cursor-pointer',
                        selectedExpiry === expiry
                          ? 'bg-foreground/10 text-foreground'
                          : 'text-foreground/60 hover:bg-foreground/5 hover:text-foreground/80',
                      ])}
                    >
                      {formatExpiryDisplay(expiry, locale)}
                      {hasPosition && (
                        <div className="absolute right-2 bottom-0.75 left-2 flex h-px overflow-hidden rounded-full">
                          {longSize > 0 && (
                            <div
                              className="bg-chart-mint h-full"
                              style={{ flex: longSize / total }}
                            />
                          )}
                          {shortSize > 0 && (
                            <div
                              className="bg-chart-red h-full"
                              style={{ flex: shortSize / total }}
                            />
                          )}
                        </div>
                      )}
                      {hasOrder && (
                        <div
                          className="absolute right-2 bottom-0.75 left-2 h-px"
                          style={{
                            backgroundImage: `linear-gradient(to right, #FFFFFF 2px, transparent 2px)`,
                            backgroundSize: '3px 1px',
                            backgroundRepeat: 'repeat-x',
                          }}
                        />
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
          <MarketStatusNotice exchange={exchange} className="max-w-40" />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto overscroll-auto" ref={scrollContainerRef}>
        {/* Sticky Header Container. Widgets have no surface of their own, so it paints the
            page backdrop itself (`bg-page`, viewport-pinned like the dashboard's) to stay opaque
            over scrolled rows yet match the gradient behind it; the flat `bg-background` read
            as a near-black band in the gradient themes. */}
        <div className="bg-page sticky top-0 z-20" style={{ minWidth: `${dynamicMinWidth}px` }}>
          {/* Call/Put Labels */}
          <div
            className="border-foreground/10 text-foreground/60 flex gap-0.5 border-y px-2 py-1.5 text-[10px]"
            style={{ minWidth: `${dynamicMinWidth}px` }}
          >
            <span className="flex-1 text-center">{t('calls')}</span>
            <span className="w-12 shrink-0" />
            <span className="flex-1 text-center">{t('puts')}</span>
          </div>

          {/* Table Header */}
          <div
            className="border-foreground/10 text-foreground/40 mb-1 flex gap-0.5 border-b py-1.75 text-[10px]"
            style={{ minWidth: `${dynamicMinWidth}px` }}
          >
            <div
              className="grid flex-1 text-center"
              style={{ gridTemplateColumns: `repeat(${visibleColCount}, minmax(0, 1fr))` }}
            >
              {visibleCallColumns.map((key) => (
                <HeaderCell key={key} columnKey={key} />
              ))}
            </div>
            <div className="w-12 shrink-0 text-center">{t('strike')}</div>
            <div
              className="grid flex-1 text-center"
              style={{
                gridTemplateColumns: `repeat(${visiblePutColumns.length}, minmax(0, 1fr))`,
              }}
            >
              {visiblePutColumns.map((key) => (
                <HeaderCell key={key} columnKey={key} />
              ))}
            </div>
          </div>
        </div>

        {/* Rows Container - Direct child of scroll container */}
        <div
          ref={rowsContainerRef}
          className="no-scrollbar relative z-0 pb-1.5"
          style={{
            minWidth: `${dynamicMinWidth}px`,
            height: sortedStrikes.length > 0 ? sortedStrikes.length * OPTION_ROW_HEIGHT : undefined,
          }}
        >
          {/* Continuous Strike Column Borders for Rows */}
          <div className="border-primary/60 pointer-events-none absolute inset-y-0 left-1/2 w-13 -translate-x-1/2 border-x border-dashed" />

          {/* Floating Underlying Price Indicator */}
          {!isLoading && underlyingPrice && selectedTicker && (
            <UnderlyingPriceIndicator
              underlyingPrice={underlyingPrice}
              sortedStrikes={sortedStrikes}
              selectedTicker={selectedTicker}
            />
          )}

          {renderedStrikes.map(({ index, strike }) => {
            const rowData = rows.get(strike)

            const callSymbol = rowData?.call?.symbol
            const putSymbol = rowData?.put?.symbol

            const callPositions = callSymbol ? positionsMap.get(callSymbol) : undefined
            const putPositions = putSymbol ? positionsMap.get(putSymbol) : undefined

            const callOrders = callSymbol ? activeOrdersMap.get(callSymbol) : undefined
            const putOrders = putSymbol ? activeOrdersMap.get(putSymbol) : undefined

            // Pass the ITM verdicts, not the raw price: the row only ever derived these
            // two booleans from it, so comparing the exact price re-rendered every row on
            // every tick even though almost none of them crossed the money.
            const hasPrice = underlyingPrice != null && underlyingPrice > 0
            const isCallItm = hasPrice && strike < underlyingPrice
            const isPutItm = hasPrice && strike > underlyingPrice

            return (
              <div
                key={strike}
                className="absolute right-0 left-0"
                style={{
                  height: OPTION_ROW_HEIGHT,
                  transform: `translateY(${index * OPTION_ROW_HEIGHT}px)`,
                }}
              >
                <OptionRow
                  strike={strike}
                  isLoading={isLoading}
                  isCallItm={isCallItm}
                  isPutItm={isPutItm}
                  call={rowData?.call}
                  put={rowData?.put}
                  cardId={cardId}
                  callPositions={callPositions}
                  putPositions={putPositions}
                  callHasBuyOrder={!!callOrders?.buy}
                  callHasSellOrder={!!callOrders?.sell}
                  putHasBuyOrder={!!putOrders?.buy}
                  putHasSellOrder={!!putOrders?.sell}
                  accountLabelById={accountLabelById}
                  optionSymbolMap={optionSymbolMap}
                  exchange={exchange}
                  onContextMenuRequest={handleContextMenuRequest}
                />
              </div>
            )
          })}
          {!selectedTicker && (
            <div className="text-foreground/20 py-10 text-center text-xs">
              {t('selectTickerToViewOptions')}
            </div>
          )}
          {selectedTicker && !selectedExpiry && (
            <div className="text-foreground/20 py-10 text-center text-xs">
              {t('selectExpiryDate')}
            </div>
          )}
          {selectedTicker && selectedExpiry && sortedStrikes.length === 0 && (
            <div className="text-foreground/20 py-10 text-center text-xs">
              {availableExpiries.length === 0 ? t('optionsNotSupported') : t('loadingOptionsData')}
            </div>
          )}
        </div>
      </div>
      {contextMenu && (
        <OptionRowContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onOpenInChart={() => {
            openInstrumentTab(
              buildOpenOptionTabArgs(contextMenu.symbol, optionSymbolMap, {
                mode: 'new-tab',
                exchange,
              })
            )
          }}
        />
      )}
    </div>
  )
}

// Memoized so a parent re-render triggered by unrelated linking-group state (this
// component subscribes narrowly via useLinkingGroupWatchList/useLinkingGroupField, but
// TabGroupedChart itself still subscribes to the full slice) doesn't cascade into
// re-rendering the option chain grid.
export default memo(OptionTable)
